// src/pages/Login.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch, setOrgContext, setAuthToken } from '../api/client';

import tealBack from '../Images/teal_dback.png';
import rypilLo from '../Images/rypilLo.png';
import rypilSyncWhite from '../Images/rypilsync_white.png';
import { renderRypilText } from '../lib/rypilText';

const PEACH = '#EBA477';

/**
 * BUSINESS MEMBERSHIPS
 * --------------------
 * We ONLY treat a user as having "business" context if /auth/me returns
 * actual brands (profile.brands) for them. Just having an AccountId or a
 * generic businessAccounts entry is NOT enough, otherwise we end up
 * with phantom businesses like "Connor" when the user is only an NFP admin.
 */
function getBusinessList(profile) {
  if (!profile) return [];

  const raw = Array.isArray(profile.brands) ? profile.brands : [];

  return raw
    .map((b) => ({
      // orgId for business dashboards = accountId (shared wallet owner),
      // but Brand membership is what makes this "business".
      id: String(profile.accountId || b.accountId || b.id || b._id || ''),
      name: b.name || 'Business',
      // require a concrete brand identifier so we know it's a real Brand
      brandId: b.brandId
        ? String(b.brandId)
        : b.id
        ? String(b.id)
        : b._id
        ? String(b._id)
        : undefined,
    }))
    .filter((x) => x.id && x.brandId);
}

/**
 * NFP MEMBERSHIPS
 * ---------------
 * Comes from nfpMemberships / causes returned by /auth/me.
 */
function getNfpList(profile) {
  if (!profile) return [];
  const raw = profile.nfpMemberships || profile.causes || [];
  return raw
    .map((c) => ({
      id: String(c.id ?? c.causeId ?? c._id ?? ''),
      name: c.publicName ?? c.legalName ?? c.name ?? 'Nonprofit',
      registrationId: c.registrationId ? String(c.registrationId) : null,
      causeId: c.causeId ? String(c.causeId) : null,
      registrationStatus: c.registrationStatus || null,
      hasDashboard: c.hasDashboard === true || !!c.causeId,
      isLive: c.isLive === true,
    }))
    .filter((x) => x.id);
}

function isDraftNfp(nf) {
  return !!nf && !nf.hasDashboard && !nf.isLive && !!(nf.registrationId || nf.registrationStatus);
}

function isDashboardNfp(nf) {
  return !!nf && (nf.hasDashboard || nf.isLive);
}

function routeToNfp(navigate, nf) {
  if (!nf) return;
  if (!isDashboardNfp(nf) && isDraftNfp(nf)) {
    setOrgContext(null);
    navigate('/nfp/register', { replace: true });
    return;
  }
  setOrgContext({ orgId: String(nf.id), orgType: 'nfp' });
  navigate('/nfp', { replace: true });
}

function getInternalOpsMembership(profile) {
  const membership = profile?.internalOpsMembership;
  if (!membership || !membership.isActive) return null;
  if (!membership.org?.id) return null;
  return {
    id: String(membership.id || ''),
    role: membership.role || '',
    orgId: String(membership.org.id),
    orgName: membership.org.name || 'Rypil Internal',
    orgSlug: membership.org.slug || null,
  };
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [chooserVisible, setChooserVisible] = useState(false);
  const [me, setMe] = useState(null);

  const togglePasswordVisibility = () => setShowPassword((p) => !p);

  async function afterLoginRoute(profile) {
    // Persist for rest of app
    localStorage.setItem('user', JSON.stringify(profile || {}));

    const businessList = getBusinessList(profile);
    const nfpList = getNfpList(profile);
    const internalOps = getInternalOpsMembership(profile);

    const hasBiz = businessList.length > 0;
    const hasNfp = nfpList.length > 0;
    const hasOps = !!internalOps;

    // Business-only → BusDash
    if (hasBiz && !hasNfp && !hasOps) {
      const acc = businessList[0];
      setOrgContext({ orgId: acc.id, orgType: 'account' });

      const brandId =
        acc.brandId ||
        profile?.suggestedBrandId ||
        profile?.primaryBrand?.brandId;

      if (brandId) {
        localStorage.setItem('lastBrandId', String(brandId));
      }

      let dest = `/accounts/${encodeURIComponent(acc.id)}/dashboard`;
      if (brandId) {
        dest += `?brandId=${encodeURIComponent(String(brandId))}`;
      }
      navigate(dest, { replace: true });
      return;
    }

    // NFP-only → NFP dashboard
    if (hasNfp && !hasBiz && !hasOps) {
      const nf = nfpList.find(isDashboardNfp) || nfpList.find(isDraftNfp) || nfpList[0];
      routeToNfp(navigate, nf);
      return;
    }

    // Internal-only ops users → Ops portal
    if (hasOps && !hasBiz && !hasNfp) {
      setOrgContext({ orgId: internalOps.orgId, orgType: 'rypil_org' });
      navigate(profile?.mustChangePassword ? '/ops/change-password' : '/ops', { replace: true });
      return;
    }

    // No orgs yet → home / onboarding hub
    if (!hasBiz && !hasNfp) {
      navigate('/home', { replace: true });
      return;
    }

    // Both present → show chooser
    setMe(profile);
    setChooserVisible(true);
  }

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');
    setSubmitting(true);
    setChooserVisible(false);
    setMe(null);

    try {
      // 1) Authenticate
      const data = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: (email || '').trim().toLowerCase(),
          password: (password || '').trim(),
        }),
      });
      if (data?.token) setAuthToken(data.token);

      // 2) Load normalized profile with memberships
      const profile = await apiFetch('/auth/me');

      // 3) Route based on memberships
      await afterLoginRoute(profile);
    } catch (e2) {
      console.error(e2);
      const status = e2?.status;
      const msg =
        e2?.data?.error ||
        e2?.data?.message ||
        (status === 401 ? 'Invalid email or password.' : e2?.message) ||
        'Please try again.';
      setErrMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Lists for chooser UI (driven by current `me` state)
  const businessList = getBusinessList(me);
  const nfpList = getNfpList(me);
  const internalOps = getInternalOpsMembership(me);

  const chooseBusiness = (acc) => {
    if (!acc?.id) return;
    setOrgContext({ orgId: String(acc.id), orgType: 'account' });

    const brandId =
      acc.brandId || me?.suggestedBrandId || me?.primaryBrand?.brandId;
    if (brandId) {
      localStorage.setItem('lastBrandId', String(brandId));
    }

    let dest = `/accounts/${encodeURIComponent(String(acc.id))}/dashboard`;
    if (brandId) {
      dest += `?brandId=${encodeURIComponent(String(brandId))}`;
    }
    navigate(dest, { replace: true });
  };

  const chooseNfp = (nf) => {
    if (!nf) return;
    routeToNfp(navigate, nf);
  };

  const chooseOps = () => {
    if (!internalOps?.orgId) return;
    setOrgContext({ orgId: internalOps.orgId, orgType: 'rypil_org' });
    navigate(me?.mustChangePassword ? '/ops/change-password' : '/ops', { replace: true });
  };

  return (
    <div
      className="login-page"
      style={{
        backgroundImage: `url(${tealBack})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        fontFamily: 'Helvetica, Arial, sans-serif',
        color: 'white',
        backgroundColor: '#042628',
        boxSizing: 'border-box',
        overflowY: 'auto',
        padding: '32px 0 40px',
      }}
    >
      {/* Login form (hidden once chooser is shown) */}
      {!chooserVisible && (
        <form
          onSubmit={handleLoginSubmit}
          className="login-form"
          style={{ width: 'min(90vw, 640px)', display: 'flex', flexDirection: 'column' }}
          noValidate
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <img
              src={rypilSyncWhite}
              alt="rypilSync"
              style={{
                height: 280,
                maxWidth: '60%',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.35))',
              }}
            />
          </div>

          <div
            style={{
              textAlign: 'center',
              maxWidth: 720,
              lineHeight: 1.45,
              margin: '0 auto',
              padding: '0 8px',
              marginTop: 'clamp(20px, 4vh, 56px)',
              marginBottom: 'clamp(20px, 4vh, 56px)',
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>
              {renderRypilText('Welcome to rypil—where profit meets purpose.')}
            </div>
            <div style={{ fontSize: 18, opacity: 0.92 }}>
              We connect brands and nonprofits so every purchase funds local impact.
            </div>
          </div>

          <div style={{ display: 'grid', gap: 16 }}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              className="login-input"
              autoComplete="username"
              required
              aria-label="Email"
              style={inputStyle}
            />

            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                autoComplete="current-password"
                required
                aria-label="Password"
                style={{ ...inputStyle, padding: '0 110px 0 28px' }}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                style={showBtnStyle}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {errMsg && (
            <div role="alert" style={alertStyle}>
              {errMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: 320,
              height: 80,
              borderRadius: 24,
              border: 'none',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 28,
              fontWeight: 600,
              color: '#1d1d1d',
              background: submitting ? '#d7b5a1' : PEACH,
              boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
              alignSelf: 'center',
              marginTop: 18,
            }}
            aria-busy={submitting}
          >
            {submitting ? 'Signing in…' : 'Submit'}
          </button>

          <Link
            to="/forgot-password"
            style={{
              marginTop: 8,
              color: 'rgba(255,255,255,0.9)',
              textDecoration: 'none',
              fontStyle: 'italic',
              fontSize: 18,
              alignSelf: 'center',
            }}
          >
            Forgot Password?
          </Link>

          <div
            className="new-accounts"
          >
            <span>New Accounts</span>
            <Link
              to="/register"
              style={{
                color: 'white',
                textDecoration: 'none',
                fontWeight: 600,
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span>Click Here</span>
              <span
                style={{
                  display: 'block',
                  width: 90,
                  height: 3,
                  borderRadius: 999,
                  background: PEACH,
                }}
              />
            </Link>
          </div>
        </form>
      )}

      {/* Context chooser (only visible if user truly has both) */}
      {chooserVisible && (
        <div style={chooserWrap}>
          <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>Choose where to go</h2>
          <p style={{ opacity: 0.9, marginTop: 6, marginBottom: 16 }}>
            We found multiple portal memberships for your account.
          </p>

          <div style={chooserGrid}>
            <div style={chooserCol}>
              <div style={chooserHeader}>Business</div>
              {businessList.map((acc) => (
                <button
                  key={acc.id}
                  type="button"
                  onClick={() => chooseBusiness(acc)}
                  style={chooserBtn}
                >
                  {acc.name}
                </button>
              ))}
              {businessList.length === 0 && (
                <div style={{ fontSize: 13, opacity: 0.7 }}>No business memberships found.</div>
              )}
            </div>

            <div style={chooserCol}>
              <div style={chooserHeader}>Nonprofit</div>
              {nfpList.map((nf) => (
                <button
                  key={nf.id}
                  type="button"
                  onClick={() => chooseNfp(nf)}
                  style={chooserBtn}
                >
                  {nf.name}
                </button>
              ))}
              {nfpList.length === 0 && (
                <div style={{ fontSize: 13, opacity: 0.7 }}>No nonprofit memberships found.</div>
              )}
            </div>

            {internalOps && (
              <div style={chooserCol}>
                <div style={chooserHeader}>Internal Ops</div>
                <button type="button" onClick={chooseOps} style={chooserBtn}>
                  {internalOps.orgName} ({internalOps.role})
                </button>
              </div>
            )}
          </div>

          <button type="button" onClick={() => setChooserVisible(false)} style={cancelBtn}>
            Back
          </button>
        </div>
      )}

      <img
        src={rypilLo}
        alt="rypil mark"
        style={{ position: 'absolute', bottom: 32, left: 32, height: 56, opacity: 0.95 }}
      />

      <style>{`
        .login-page{
          min-height:100vh;
          min-height:100dvh;
        }
        .login-input::placeholder{color:rgba(255,255,255,0.75);}
        .login-form{
          position:relative;
          padding-bottom:140px;
          margin:32px auto;
          flex-shrink:0;
        }
        .new-accounts{
          position:absolute;
          left:0;
          bottom:48px;
          width:100%;
          display:flex;
          flex-direction:column;
          align-items:center;
          gap:6px;
          font-size:20px;
          color:white;
          text-align:center;
        }
        @media (max-width: 640px), (max-height: 900px){
          .login-page{align-items:flex-start;}
          .login-form{padding-bottom:24px;}
          .new-accounts{position:static;margin-top:20px;}
        }
      `}</style>
    </div>
  );
}

/* ---------------- styles ---------------- */
const inputStyle = {
  width: '100%',
  height: 72,
  borderRadius: 20,
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.10)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  color: 'white',
  fontSize: 24,
  padding: '0 28px',
  outline: 'none',
  boxSizing: 'border-box',
};
const showBtnStyle = {
  position: 'absolute',
  top: '50%',
  right: 18,
  transform: 'translateY(-50%)',
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.85)',
  fontSize: 18,
  cursor: 'pointer',
  padding: '8px 12px',
};
const alertStyle = {
  marginTop: 12,
  textAlign: 'center',
  fontSize: 16,
  background: 'rgba(0,0,0,0.35)',
  padding: '10px 14px',
  borderRadius: 12,
};

const chooserWrap = {
  width: 'min(92vw, 760px)',
  padding: 20,
  borderRadius: 16,
  background: 'rgba(255,255,255,0.10)',
  border: '1px solid rgba(255,255,255,0.08)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  color: 'white',
  textAlign: 'left',
};
const chooserGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
  marginTop: 8,
};
const chooserCol = {
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 12,
  padding: 12,
  background: 'rgba(255,255,255,0.06)',
};
const chooserHeader = { fontWeight: 700, marginBottom: 8, opacity: 0.95, fontSize: 14 };
const chooserBtn = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.10)',
  color: 'white',
  cursor: 'pointer',
  fontSize: 14,
  marginBottom: 8,
};
const cancelBtn = {
  marginTop: 16,
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.4)',
  color: 'white',
  padding: '10px 16px',
  borderRadius: 12,
  cursor: 'pointer',
};
